<script setup>
import {ref, onMounted, computed, onUnmounted, watch} from "vue";
import {useMainOrdersStore} from "@/stores/mainOrders.js";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import {gregorianDate} from "@/utils/gregorianDate.js";
import StateColumn from "@/views/orders/Shared/StateColumn.vue";
import {formatPrice} from "@/utils/priceFormatter.js";
import Filters from "@/views/orders/Filters/index.vue";
import Breadcrumbs from "@/components/Breadcrumbs/index.vue";
import {Icon} from "@iconify/vue";
import AllButtons from "@/views/orders/Shared/AllButtons.vue";
import {showMessage} from "@/utils/showMessage.js";
import {useOrderFilter} from "@/composables/useOrderFilter.js";
import {useRouter} from "vue-router";
import SortIcon from "@/components/datatable/sortIcon.vue";
import {useFilterByPaymentMethods} from "@/composables/useFilterByPaymentMethods.js";

const router = useRouter();
let interval = null;
const cleared = ref(false);
const search = computed(() => ordersStore.currencyBuyOrdersSearch);
const accountTitleMap = computed(() => Object.fromEntries(ordersStore.gameAccounts.map(acc => [acc.id, acc.title])));
const getAccountTitle = computed(() => (accountId) => accountTitleMap.value[accountId] || 'Disabled Account');
const ordersStore = useMainOrdersStore();
const orders = computed(() => {
  return ordersStore.getOrders('currencyBuy').map(order => ({
    ...order,
    amount: order.amount || order.amount === 0
        ? Number(order.amount)
        : null,
    price: order.price || order.price === 0
        ? Number(order.price)
        : null,
    tomanPrice: order.tomanPrice || order.tomanPrice === 0
        ? Number(order.tomanPrice)
        : null,
    value: order.value || order.value === 0
        ? Number(order.value)
        : null,
    tomanValue: order.tomanValue || order.tomanValue === 0
        ? Number(order.tomanValue)
        : null,
    accountTitle: getAccountTitle.value(order.account)
  }))
});

const fetchNewOrders = async () => {
  if (cleared.value) return;

  if (orders.value.length > 0 && !ordersStore.isGettingNewOrders) {
    await ordersStore.action_getOrders('CurrencyBuy');
  }

  if (!cleared.value) {
    interval = setTimeout(fetchNewOrders, 2000);
  }
};

async function applyFilters() {
  ordersStore.reset_lastUpdate();
  ordersStore.reset_orders('currencyBuyOrders');
  await ordersStore.action_getOrders('CurrencyBuy');
}

function orderDetails({data}) {
  const route = {name: 'currency-buy-order-details', params: {id: 0}}
  if (data.uuid) {
    route.params.id = data.uuid
  } else {
    route.params.id = data.id
  }
  router.push(route)
}

function onSortChange(params) {
  const sortOrder = params.sortOrder === 1 ? 'asc' : 'desc';
  ordersStore.setCurrencyBuyOrdersSort(params.sortField, sortOrder);
}

const {filtered} = useOrderFilter(
    orders,
    search,
    'currencyBuy'
);

const {filteredByPaymentMethod: filteredOrdersByPaymentMethod} = useFilterByPaymentMethods(
    filtered,
    computed(() => ordersStore.paymentMethodsFilter),
    "paymentMethod"
);

function copyUUIDs() {
  const selectedOrders = ordersStore.selectedCurrencyBuyOrders.length > 0 ? ordersStore.selectedCurrencyBuyOrders : filteredOrdersByPaymentMethod.value

  if (!selectedOrders.length) {
    alert("No UUIDs to copy!");
    return;
  }
  const uuids = selectedOrders
      .map(order => order.uuid)
      .filter(Boolean)
      .join('\n');

  navigator.clipboard.writeText(uuids)
      .then(() => {
        showMessage(selectedOrders.length === 1 ? `${selectedOrders.length} UUID copied` : `${selectedOrders.length} UUIDs copied`, 'success');
      })
      .catch(err => {
        console.error("Failed to copy UUIDs:", err);
        showMessage('Failed to copy UUIDs', 'error');
      });
}

watch(filteredOrdersByPaymentMethod, (newVal) => {
  ordersStore.currencyBuyFilteredOrders = newVal;
});

onMounted(() => {
  ordersStore.action_getGameAccounts();
  ordersStore.action_getOrders('CurrencyBuy');
  fetchNewOrders();
})

onUnmounted(() => {
  clearTimeout(interval);
  cleared.value = true
});
</script>

<template>
  <div class="h-full">
    <div class="flex flex-col gap-4 w-full h-full">
      <div class="flex flex-row justify-between gap-4">
        <Breadcrumbs/>
        <AllButtons summaryPermission="ORDERS_CURRENCY_ACCESS_SUMMARY" addOrderPermission="ORDERS_CURRENCY_UPDATE"
                    addOrderRoute="currency-buy-order-add" summary="CurrencyBuySummary" :applyFilter="applyFilters"
                    v-model="ordersStore.currencyBuyOrdersSearch" @copyUUIDs="copyUUIDs"/>
      </div>
      <div v-if="ordersStore.isLoading" class="flex items-center justify-center h-60">
        <Icon icon="eos-icons:bubble-loading" class="size-12"/>
      </div>
      <DataTable v-else
                 :nullSortOrder="-1"
                 :value="filteredOrdersByPaymentMethod"
                 scrollable
                 scrollHeight="flex"
                 :virtualScrollerOptions="{ itemSize: 32 }"
                 resizableColumns
                 removableSort
                 stripedRows
                 class="text-xs w-full"
                 size="small"
                 tableStyle="width: 100%; table-layout: fixed;"
                 :sortField="ordersStore.currencyBuyOrdersSort?.field"
                 :sortOrder="ordersStore.currencyBuyOrdersSort?.type === 'asc' ? 1 : -1"
                 row-hover
                 @row-click="orderDetails"
                 @sort="onSortChange"
                 dataKey="id"
                 v-model:selection="ordersStore.selectedCurrencyBuyOrders"
                 :rowClass="() => 'hover:!bg-[#2c4967] even:!bg-[#1A2941] odd:!bg-[#121E31] !text-slate-300'"
      >
        <Column selectionMode="multiple" style="width: 36px;" headerStyle="width: 36px !important"></Column>
        <Column field="uuid" header="UUID" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap !py-0"
                style="width: 10%; min-width: 100px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            <span @click.stop.prevent class="w-full block h-full py-[0.375rem]">{{ slotProps.data.uuid }}</span>
          </template>
        </Column>
        <Column field="game" header="Game" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 10%; min-width: 100px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
        </Column>
        <Column field="server" header="Server" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 10%; min-width: 100px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
        </Column>
        <Column field="accountTitle" header="Account" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 10%; min-width: 100px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
        </Column>
        <Column field="amount" header="Amount" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 8%; min-width: 80px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
        </Column>
        <Column field="price" header="Price" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 10%; min-width: 100px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            {{ slotProps.data.price || slotProps.data.price === 0 ? formatPrice(slotProps.data.price) + ' $' : '' }}
          </template>
        </Column>
        <Column field="tomanPrice" header="TMN Price" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 10%; min-width: 100px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            {{ slotProps.data.tomanPrice ? formatPrice(slotProps.data.tomanPrice, 0) + ' Tmn' : '' }}
          </template>
        </Column>
        <Column field="value" header="Value" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 8%; min-width: 80px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            {{ slotProps.data.value || slotProps.data.value === 0 ? formatPrice(slotProps.data.value, 2) + ' $' : '' }}
          </template>
        </Column>
        <Column field="tomanValue" header="TMN Value" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 8%; min-width: 80px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            {{
              slotProps.data.tomanValue || slotProps.data.tomanValue === 0 ? formatPrice(slotProps.data.tomanValue, 0) + ' Tmn' : ''
            }}
          </template>
        </Column>
        <Column field="paymentMethod" header="Payment Method" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 8%; min-width: 80px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
        </Column>
        <Column field="takerName" header="Supplier" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 8%; min-width: 80px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            {{
              slotProps.data.takerName
                  ? slotProps.data.takerName
                  : slotProps.data.supplier ? slotProps.data.supplier : ''
            }}
          </template>
        </Column>
        <Column field="state" header="Status" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 8%; min-width: 80px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            <StateColumn :state="slotProps.data.state"/>
          </template>
        </Column>
        <Column field="lastUpdate" header="Last Update" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 8%; min-width: 100px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
          <span class="block w-full max-w-max">
            {{ gregorianDate(slotProps.data.lastUpdate, 'DD MMM HH:mm') }}
          </span>
          </template>
        </Column>
        <Column field="creationTime" header="Creation Time" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 8%; min-width: 100px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            <span class="block w-full max-w-max">
              {{ gregorianDate(slotProps.data.creationTime, 'DD MMM YYYY') }}
            </span>
          </template>
        </Column>
        <template #empty>
          <h1 class="py-2 text-center text-lg">No Data</h1>
        </template>
      </DataTable>
    </div>
    <Filters v-if="ordersStore.isShowFilterModal" @applyFilter="applyFilters"
             :filters="['games', 'states', 'paymentMethods']" service="Currency"/>
  </div>
</template>